import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, filter, groupBy, map, mergeMap, scan, take, tap } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { TrashToolSegmentType } from '@ninety/platform/models/analytics/segment/trash-tool-segment-type.enum';
import { IntervalCode } from '@ninety/platform/models/issues/interval-code';
import { FilterServiceActions } from '@ninety/platform/services';
import {
  LayoutsIssuesEvents,
  selectCompanyId,
  selectIsInMeeting,
  teamAll,
  TeamListStateActions,
  TeamSelectors,
} from '@ninety/platform/state';
import { selectCurrentUserId } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { trackArchiveViewToggled } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.helpers';
import { selectArchiveTrackingMeeting } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.selectors';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import {
  trackTrashDeleteForever,
  trackTrashRestored,
  trackTrashViewed,
} from '@ninety/platform/state/app-global/analytics/segment/trash-segment.helpers';

import { IssuesActions } from './issues.actions';
import * as IssuesSelectors from './issues.selectors';
import { IssuesTrashApiActions } from './trash/trash.actions';

/** Fields on an issue update change set already owned by a dedicated issue event, so
 *  issue_updated excludes them and won't double-fire: completion (issue_short_term_set_
 *  completed / issue_long_term_completed) + completedDate; archive (issue_archived /
 *  issue_unarchived) + archivedDate; working team (issue_sent_to_team); priority
 *  (issue_priority_changed — the `rating` field); plus intervalCode and ordinal
 *  (move/reorder, not "edit"). Defensive — current updateIssue dispatch sites only pass
 *  content fields; this list guards against future paths that route a dedicated field
 *  through updateIssue. */
const ISSUE_DEDICATED_UPDATE_FIELDS = [
  'completed',
  'completedDate',
  'archived',
  'archivedDate',
  'workingTeamId',
  'rating',
  'intervalCode',
  'ordinal',
];

@Injectable()
export class IssuesAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  trackIssueCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.createIssueSuccess, IssuesActions.saveInlineSuccess),
      map(action => {
        const { issue } = action;
        const eventSource = action.type === IssuesActions.saveInlineSuccess.type ? 'inline' : 'regular';

        return SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_CREATED,
          params: {
            entityId: issue._id,
            companyId: issue.companyId,
            intervalCode: issue.intervalCode,
            isPublic: issue.isPublic,
            rating: issue.rating,
            teamId: issue.teamId,
            userId: issue.userId,
            eventSource,
          },
        });
      })
    )
  );

  trackCompletedIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setCompletedStateSuccess),
      filter(({ issue }) => issue.intervalCode !== IntervalCode.longTerm && issue.completed),
      concatLatestFrom(() => this.store.select(selectIsInMeeting)),
      map(([{ issue }, inMeeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_SHORT_TERM_SET_COMPLETED,
          params: {
            entityId: issue._id,
            companyId: issue.companyId ?? '',
            teamId: issue.teamId,
            userId: issue.userId,
            eventSource: inMeeting ? 'meeting' : 'issues page',
            issueCompletedFlag: issue.completed,
            issueCompletedDate: issue.completedDate,
          },
        })
      )
    )
  );

  /** Issue Long Term Completed — mirrors trackCompletedIssue$, filtered to the long-term
   *  interval. Same setCompletedStateSuccess source; eventSource derived from selectIsInMeeting. */
  trackLongTermCompletedIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setCompletedStateSuccess),
      filter(({ issue }) => issue.intervalCode === IntervalCode.longTerm && issue.completed),
      concatLatestFrom(() => this.store.select(selectIsInMeeting)),
      map(([{ issue }, inMeeting]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_LONG_TERM_COMPLETED,
          params: {
            entityId: issue._id,
            companyId: issue.companyId ?? '',
            teamId: issue.teamId,
            userId: issue.userId,
            eventSource: inMeeting ? 'meeting' : 'issuesPage',
            completedDate: issue.completedDate,
          },
        })
      )
    )
  );

  /** Issue Sent To Team — fires on sendToTeamSuccess. Emits source teamId (the issue's
   *  original team), destination workingTeamId (the team it was sent to), and issue context. */
  trackIssueSentToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendToTeamSuccess),
      map(({ issue }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_SENT_TO_TEAM,
          params: {
            entityId: issue._id,
            companyId: issue.companyId ?? '',
            fromTeamId: issue.teamId,
            toTeamId: issue.workingTeamId,
            userId: issue.userId,
          },
        })
      )
    )
  );

  /** Issue Team Reassigned — the detail-panel Team dropdown changes an issue's `teamId`
   *  outright (a "move to another team" semantic, distinct from issue_sent_to_team, which sets
   *  `workingTeamId` and preserves `teamId` — a "lend to another team" semantic). Without this
   *  event the reassignment was silently untracked.
   *
   *  `updateTeamSuccess` carries only `{ issueId, teamId }`, and the reducer removes the issue
   *  from the current-team list on that action, so neither the success action nor a success-time
   *  store lookup can supply the prior team. `fromTeamId` is therefore captured at `updateTeam`
   *  (intent) time — when the issue is still in the store — then the effect waits for the matching
   *  terminal action and emits only on success, so a reassignment that fails (updateTeamFailure)
   *  records nothing. `toTeamId` is read from the success action (the persisted destination).
   *
   *  The terminal action is correlated on BOTH issueId and the intended teamId: the detail view
   *  closes and the issue is removed on success so a same-issue re-dispatch is already unlikely,
   *  but if one slips through, correlating on teamId keeps each intent waiting for its own
   *  destination rather than letting a single success satisfy two intents' take(1) (which would
   *  double-emit with the wrong toTeamId). The issueId half of the filter separates concurrent
   *  reassignments of other issues. Trade-off: because updateTeam$ uses switchMap, a superseded
   *  same-issue intent's request is cancelled and never produces a terminal, so that intent's
   *  inner subscription waits forever — a tiny, bounded leak (one idle subscription per cancelled
   *  rapid re-dispatch). Accepted over the prior double-emit; not worth a race()-based teardown.
   *
   *  The nested actions$.pipe is an intentional request→response correlation — capture the
   *  prior-team state at intent, then gate on the matching terminal. It is deliberately not the
   *  flat shape of the other effects in this file (those don't need pre-mutation state); don't
   *  flatten it. */
  trackIssueTeamReassigned$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTeam),
      concatLatestFrom(({ issueId }) => this.store.select(IssuesSelectors.selectIssueById(issueId))),
      mergeMap(([{ issueId, teamId: intendedTeamId }, issue]) => {
        const fromTeamId = issue?.teamId;
        return this.actions$.pipe(
          ofType(IssuesActions.updateTeamSuccess, IssuesActions.updateTeamFailure),
          filter(action => action.issueId === issueId && action.teamId === intendedTeamId),
          take(1),
          filter(action => action.type === IssuesActions.updateTeamSuccess.type),
          map(action =>
            SegmentActions.track({
              event: SegmentTrackEvent.ISSUE_TEAM_REASSIGNED,
              params: {
                entityId: issueId,
                fromTeamId: fromTeamId ?? null,
                toTeamId: action.teamId,
              },
            })
          )
        );
      })
    )
  );

  /** Issue Deleted — fires post-confirmation (deleteIssueSuccess carries only the issueId). */
  trackIssueDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteIssueSuccess),
      map(({ issueId }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_DELETED,
          params: {
            entityId: issueId,
          },
        })
      )
    )
  );

  /** Issue Archived — fires on both archive paths: the plain Archive action
   *  (archiveIssueSuccess, which carries the issue's `completed` flag at archive time) and
   *  the Complete and Archive action (completeAndArchiveIssueSuccess, which only carries
   *  `issueId` and is by definition `completed: true`). Discrimination is on action identity
   *  (action.type) rather than prop presence so a future dispatcher that omits the optional
   *  `completed` from archiveIssueSuccess still emits `completed: false` (preserves the
   *  "plain archive" semantic) rather than silently defaulting to `true`. The `completed`
   *  flag on the event lets BI distinguish "complete and archive" from "archive without
   *  completing." */
  trackIssueArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssueSuccess, IssuesActions.completeAndArchiveIssueSuccess),
      map(action =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_ARCHIVED,
          params: {
            entityId: action.issueId,
            completed: action.type === IssuesActions.completeAndArchiveIssueSuccess.type ? true : !!action.completed,
          },
        })
      )
    )
  );

  trackIssueUnarchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.unarchiveIssueSuccess),
      map(({ issueId }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_UNARCHIVED,
          params: {
            entityId: issueId,
          },
        })
      )
    )
  );

  /** Issue Updated — generic edit (title, description, owner, etc.) on updateIssueSuccess.
   *  Fires only when the change set includes a field NOT already owned by a dedicated event.
   *  Debounced **per-issue** via groupBy(issue._id), with a per-group buffer that UNIONs
   *  changedFields across the 1500 ms window before emitting on pause — a typing session
   *  that touched title + description reports both fields, and issue A's emit isn't dropped
   *  when issue B is edited within the same window. The groupBy duration selector closes
   *  inactive groups after 60 s + 1500 ms to avoid unbounded Subject retention.
   *
   *  updateIssueSuccess is the API-success terminator (carries both the persisted issue and
   *  the changes diff), so issue effects are success-gated by construction — no intent-vs-
   *  success gap of the kind documented on rock_updated / milestone_updated. Mirrors the
   *  per-group accumulator shape from those effects. */
  trackIssueUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateIssueSuccess),
      map(({ issue, changes }) => ({
        issue,
        changedFields: Object.keys(changes ?? {}).filter(key => !ISSUE_DEDICATED_UPDATE_FIELDS.includes(key)),
      })),
      filter(({ changedFields }) => changedFields.length > 0),
      groupBy(({ issue }) => issue._id, { duration: g$ => g$.pipe(debounceTime(60_000 + 1500)) }),
      mergeMap(group$ => {
        // Per-issue accumulator. Invariant: `buffer` holds exactly the items that landed
        // since the last debounceTime(1500) emission for this group. (Same shape as the
        // rock_updated / milestone_updated accumulators.)
        let buffer: { changedFields: string[] }[] = [];
        return group$.pipe(
          tap(item => buffer.push(item)),
          debounceTime(1500),
          map(latest => {
            const unioned = Array.from(new Set(buffer.flatMap(item => item.changedFields)));
            buffer = [];
            return { issue: latest.issue, changedFields: unioned };
          })
        );
      }),
      map(({ issue, changedFields }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_UPDATED,
          params: {
            entityId: issue._id,
            companyId: issue.companyId ?? '',
            teamId: issue.teamId,
            userId: issue.userId,
            changedFields: changedFields.join(','),
          },
        })
      )
    )
  );

  /** Issue Priority Changed — fires when an issue's priority (the nullable 1–5 `rating`
   *  field; None = null) changes. Priority has no dedicated action: both the team-issues
   *  list dropdown and the issue card dispatch updateIssue({ changes: { rating } }), so
   *  `rating` is carved out of issue_updated (see ISSUE_DEDICATED_UPDATE_FIELDS) and reported
   *  as its own event. updateIssueSuccess carries the persisted issue + the changes diff, so
   *  newValue comes straight from the action payload (no store sampling → no reducer race) and
   *  the effect is success-gated by construction. Clearing priority to None emits
   *  newValue: null — a valid change. Shares the issue_updated entity shape (companyId/teamId/
   *  userId off the issue owner, not the actor) so the two effects join cleanly downstream.
   *  Unlike issue_updated, this is NOT debounced: each priority selection is a discrete,
   *  meaningful change with its own newValue, so a rapid 3→4→5 intentionally emits three
   *  events — don't "fix" it into a debounce. */
  trackIssuePriorityChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateIssueSuccess),
      filter(({ changes }) => !!changes && 'rating' in changes),
      map(({ issue, changes }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_PRIORITY_CHANGED,
          params: {
            entityId: issue._id,
            companyId: issue.companyId ?? '',
            teamId: issue.teamId,
            userId: issue.userId,
            newValue: changes.rating ?? null,
          },
        })
      )
    )
  );

  /** Only on closed → open; redundant showTrashItems must not double-fire View Trash. */
  trackTrashViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsIssuesEvents.showTrashItems, LayoutsIssuesEvents.hideTrashItems),
      scan(
        (acc, action) => {
          const show = action.type === LayoutsIssuesEvents.showTrashItems.type;
          const shouldTrack = !acc.previous && show;
          return { previous: show, shouldTrack };
        },
        { previous: false, shouldTrack: false }
      ),
      filter(({ shouldTrack }) => shouldTrack),
      map(() => trackTrashViewed(TrashToolSegmentType.Issues))
    )
  );

  trackTrashRestored$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.restoreSuccess),
      map(({ id }) => trackTrashRestored(TrashToolSegmentType.Issues, id))
    )
  );

  trackTrashDeleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.deleteForeverSuccess),
      map(({ id }) => trackTrashDeleteForever(TrashToolSegmentType.Issues, id))
    )
  );

  /** Issue List View Changed — the header toggle that switches the issues list between the
   *  single-column list view and the two-column view. Listens to listViewToggleClicked (the
   *  user's click), not setListView, so programmatic switches like forceListViewOnVtoRoute$
   *  are never tracked. */
  trackListViewChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.listViewToggleClicked),
      concatLatestFrom(() => [
        this.store.select(selectCompanyId),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectCurrentUserId),
      ]),
      map(([{ listView }, companyId, teamId, userId]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_LIST_VIEW_CHANGED,
          params: { listView, companyId, teamId, userId },
        })
      )
    )
  );

  trackAllTeamsFilterSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.getSelectedTeam),
      filter(({ id }) => id === teamAll._id),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectIsIssuesSegmentInUrl)),
      filter(([, isInIssues]) => isInIssues),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.ISSUE_ALL_TEAMS_FILTER_SELECTED }))
    )
  );

  trackArchiveToggleClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.archiveToggleClicked),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIsIssuesArchiveToggleUrl),
        this.store.select(IssuesSelectors.selectIntervalCode),
        this.store.select(selectArchiveTrackingMeeting),
      ]),
      filter(([, isInIssues]) => isInIssues),
      map(([{ showArchived }, , intervalCode, meeting]) =>
        trackArchiveViewToggled({
          itemType: 'issue',
          control: 'toggle',
          isArchived: showArchived,
          pageSource: 'issuesPage',
          meeting,
          intervalCode,
        })
      )
    )
  );

  trackTeamIssueVotingToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamListStateActions.updateSettingsSuccess),
      filter(({ update }) => update.issueVoting !== undefined),
      map(({ teamId, update }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_TEAM_VOTING_TOGGLED,
          params: {
            entityId: teamId,
            teamId,
            isVotingEnabled: update.issueVoting,
          },
        })
      )
    )
  );
}
